// A theme's colors from a picture: the picture's own tones, found by grouping
// its pixels, then placed the way the theme guide asks an agent to place them.
// Tile and background from its deepest tone (its palest, for a light picture),
// ink near white or near black, accent from its most vivid light, charts from
// its other colors. The board's contrast guard then keeps every word readable.

import type { ThemeColors } from "../../shared/types";

type RGB = [number, number, number];

const toHex = (c: RGB) => "#" + c.map((v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, "0")).join("");
const lin = (v: number) => {
  const s = v / 255;
  return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
};
const lum = (c: RGB) => 0.2126 * lin(c[0]) + 0.7152 * lin(c[1]) + 0.0722 * lin(c[2]);
const contrast = (a: RGB, b: RGB) => {
  const [x, y] = [lum(a), lum(b)].sort((p, q) => q - p);
  return (x + 0.05) / (y + 0.05);
};
const mix = (a: RGB, b: RGB, t: number): RGB => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
const chroma = (c: RGB) => (Math.max(...c) - Math.min(...c)) / 255;

function hsl([r, g, b]: RGB): [number, number, number] {
  const R = r / 255, G = g / 255, B = b / 255;
  const max = Math.max(R, G, B), min = Math.min(R, G, B);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  const h = max === R ? (G - B) / d + (G < B ? 6 : 0) : max === G ? (B - R) / d + 2 : (R - G) / d + 4;
  return [h / 6, s, l];
}

function fromHsl(h: number, s: number, l: number): RGB {
  if (s === 0) return [l * 255, l * 255, l * 255];
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const f = (t: number) => {
    t = ((t % 1) + 1) % 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  return [f(h + 1 / 3) * 255, f(h) * 255, f(h - 1 / 3) * 255];
}

/** The same hue at another lightness and a share of its saturation. */
const relight = (c: RGB, l: number, sat = 1): RGB => {
  const [h, s] = hsl(c);
  return fromHsl(h, Math.min(1, s * sat), l);
};

interface Cluster {
  c: RGB;
  w: number;
}

/** The picture's main colors, by k-means over its pixels, heaviest first. */
function clusters(px: RGB[], k = 7): Cluster[] {
  if (!px.length) return [];
  // spread the first centers out: each next one is the pixel furthest from those chosen
  const centers: RGB[] = [px[Math.floor(px.length / 2)]];
  const d2 = (a: RGB, b: RGB) => (a[0] - b[0]) ** 2 + (a[1] - b[1]) ** 2 + (a[2] - b[2]) ** 2;
  while (centers.length < k) {
    let best = px[0];
    let far = -1;
    for (let i = 0; i < px.length; i += 3) {
      const m = Math.min(...centers.map((c) => d2(c, px[i])));
      if (m > far) {
        far = m;
        best = px[i];
      }
    }
    centers.push(best);
  }
  const label = new Int32Array(px.length);
  for (let it = 0; it < 10; it++) {
    const sum = centers.map(() => [0, 0, 0, 0]);
    for (let i = 0; i < px.length; i++) {
      let bi = 0;
      let bd = Infinity;
      for (let j = 0; j < centers.length; j++) {
        const d = d2(px[i], centers[j]);
        if (d < bd) {
          bd = d;
          bi = j;
        }
      }
      label[i] = bi;
      const s = sum[bi];
      s[0] += px[i][0];
      s[1] += px[i][1];
      s[2] += px[i][2];
      s[3]++;
    }
    sum.forEach((s, j) => {
      if (s[3]) centers[j] = [s[0] / s[3], s[1] / s[3], s[2] / s[3]];
    });
  }
  const weight = centers.map(() => 0);
  for (let i = 0; i < px.length; i++) weight[label[i]]++;
  return centers.map((c, j) => ({ c, w: weight[j] / px.length })).filter((x) => x.w > 0).sort((a, b) => b.w - a.w);
}

export interface PictureLook {
  mode: "dark" | "light";
  colors: ThemeColors;
  /** the tone where the tiles do not cover it: the top, and the sides */
  tone: string;
}

/** A whole look from a picture's pixels, as RGBA bytes of a small copy of it. */
export function lookFromPixels(data: Uint8ClampedArray, w: number, h: number): PictureLook {
  const px: RGB[] = [];
  let edge: RGB = [0, 0, 0];
  let edges = 0;
  for (let y = 0; y < h; y++)
    for (let x = 0; x < w; x++) {
      const o = (y * w + x) * 4;
      const c: RGB = [data[o], data[o + 1], data[o + 2]];
      px.push(c);
      if (y < h * 0.3 || x < w * 0.15 || x > w * 0.85) {
        edge = [edge[0] + c[0], edge[1] + c[1], edge[2] + c[2]];
        edges++;
      }
    }
  const tone: RGB = edges ? [edge[0] / edges, edge[1] / edges, edge[2] / edges] : [20, 20, 30];
  const groups = clusters(px);
  const avg = groups.reduce((a, g) => a + lum(g.c) * g.w, 0);
  const dark = avg < 0.32 || lum(tone) < 0.28;
  const byLight = [...groups].sort((a, b) => lum(a.c) - lum(b.c));
  const deepest = byLight.find((g) => g.w >= 0.05) ?? byLight[0];
  const palest = [...byLight].reverse().find((g) => g.w >= 0.05) ?? byLight[byLight.length - 1];
  const tile = dark ? relight(deepest.c, 0.1, 0.55) : relight(palest.c, 0.95, 0.35);
  const bg = dark ? relight(deepest.c, 0.06, 0.5) : relight(palest.c, 0.9, 0.4);
  const ink = dark ? mix([255, 255, 255], palest.c, 0.08) : mix([16, 16, 20], deepest.c, 0.12);
  // the color that pops is the picture's own light: a lamp, a sunset, a sign. Its most vivid bright
  // pixels (for a light picture, its most vivid deep ones), grouped so two lights never blend to mud
  const score = (c: RGB) => (dark ? chroma(c) * (0.3 + lum(c)) : chroma(c) * (1.25 - lum(c)));
  const top = [...px].sort((x, y) => score(y) - score(x)).slice(0, Math.max(12, Math.round(px.length * 0.03)));
  // a small vivid light beats a big pale one: the accent is what pops, not what covers most
  const rank = (g: Cluster) => score(g.c) * chroma(g.c) * g.w ** 0.25;
  const spots = clusters(top, 3).sort((x, y) => rank(y) - rank(x));
  const spot = spots[0];
  let accent: RGB = spot && chroma(spot.c) >= 0.18 ? spot.c : dark ? relight(palest.c, 0.78, 1.4) : relight(deepest.c, 0.32, 1.4);
  for (let i = 0; i < 20 && contrast(accent, tile) < 3.2; i++) accent = mix(accent, dark ? [255, 255, 255] : [0, 0, 0], 0.08);
  // charts that tell apart: the picture's other colors first, then hues spaced around the accent
  const hueOf = (c: RGB) => hsl(c)[0];
  const gap = (x: number, y: number) => {
    const d = Math.abs(x - y) % 1;
    return Math.min(d, 1 - d);
  };
  const chosen: RGB[] = [accent];
  for (const c of [...spots.slice(1).map((g) => g.c), ...groups.filter((g) => chroma(g.c) >= 0.08).map((g) => g.c)]) {
    if (chosen.length >= 5) break;
    if (chosen.every((x) => gap(hueOf(x), hueOf(c)) >= 0.09)) chosen.push(fromHsl(hueOf(c), Math.min(0.68, hsl(c)[1] * 1.2), dark ? 0.66 : 0.42));
  }
  const [ah, as] = hsl(accent);
  for (const step of [0.5, 0.17, 0.83, 0.33, 0.67]) {
    if (chosen.length >= 5) break;
    const hue = (ah + step) % 1;
    if (chosen.every((x) => gap(hueOf(x), hue) >= 0.09)) chosen.push(fromHsl(hue, Math.max(0.45, Math.min(0.68, as)), dark ? 0.66 : 0.42));
  }
  const charts = chosen.slice(0, 5).map(toHex);
  return {
    mode: dark ? "dark" : "light",
    colors: { bg: toHex(bg), tile: toHex(tile), ink: toHex(ink), accent: toHex(accent), ...(charts.length > 1 ? { charts } : {}) },
    tone: toHex(tone),
  };
}

/** A name from a file's: words, not a camera's numbering. */
export function nameFromFile(file: string): string {
  const base = file.replace(/\.[a-z0-9]{2,5}$/i, "").replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim();
  if (!base || /^(img|dsc|dscn|pxl|photo|image|screenshot|screen shot|wp|mvimg|p)\s*\d/i.test(base) || /^[\d\s]+$/.test(base) || base.length > 40) return "My photo";
  return base.replace(/\b\w/g, (m) => m.toUpperCase());
}
